import { useEffect, useState } from 'react';
import { useI18n } from '@/components/_vs/i18n/I18nProvider';
import { useConsent } from './ConsentProvider';

const toggleTrackCls = 'relative w-10 h-6 rounded-full transition-colors shrink-0';
const toggleThumbCls = 'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform shadow-sm';

function CategoryToggle({ label, description, checked, disabled, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-theme-border last:border-b-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-theme-text">{label}</p>
        <p className="text-xs text-theme-text-muted mt-0.5 leading-relaxed">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={[toggleTrackCls, checked ? 'bg-theme-accent' : 'bg-theme-border', disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'].join(' ')}
      >
        <span className={toggleThumbCls + (checked ? ' translate-x-4' : '')} />
      </button>
    </div>
  );
}

function PreferencesModal({ onClose }) {
  const { t } = useI18n();
  const { consent, savePreferences } = useConsent();
  const [draft, setDraft] = useState(consent);

  useEffect(() => setDraft(consent), [consent]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('cookie.preferencesTitle')}
      onClick={onClose}
      // z-index sopra 1000: i controlli di Leaflet (blocco Mappa — zoom,
      // attribuzione) hanno z-index:1000 nel proprio CSS e altrimenti
      // finiscono sopra qualsiasi overlay del sito con z-index più basso.
      className="fixed inset-0 z-[1200] flex items-end sm:items-center justify-center bg-black/50 p-4"
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-xl bg-theme-bg-surface border border-theme-border p-6 shadow-vs-md">
        <h2 className="text-base font-medium text-theme-text mb-1">{t('cookie.preferencesTitle')}</h2>
        <p className="text-xs text-theme-text-muted leading-relaxed mb-2">{t('cookie.message')}</p>

        <div className="mt-2">
          <CategoryToggle label={t('cookie.necessaryLabel')} description={t('cookie.necessaryDesc')} checked disabled onChange={() => {}} />
          <CategoryToggle
            label={t('cookie.statisticsLabel')}
            description={t('cookie.statisticsDesc')}
            checked={!!draft.statistics}
            onChange={(v) => setDraft((d) => ({ ...d, statistics: v }))}
          />
          <CategoryToggle
            label={t('cookie.marketingLabel')}
            description={t('cookie.marketingDesc')}
            checked={!!draft.marketing}
            onChange={(v) => setDraft((d) => ({ ...d, marketing: v }))}
          />
        </div>

        <div className="flex items-center justify-end gap-3 mt-5">
          <button onClick={onClose} className="text-xs font-medium text-theme-text-muted hover:text-theme-text transition-colors">
            {t('cookie.close')}
          </button>
          <button
            onClick={() => savePreferences(draft)}
            className="inline-flex items-center px-5 py-2.5 rounded-lg bg-theme-accent text-theme-accent-text text-sm font-medium tracking-wide"
          >
            {t('cookie.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Banner iniziale (solo finché `decided` è false) + modale preferenze
 * (riapribile in ogni momento dal link "Preferenze cookie" in footer, via
 * `openPreferences()` del ConsentProvider). Il blocco reale del contenuto
 * non essenziale avviene altrove (`ConsentGate`), qui c'è solo la UI.
 */
export function CookieBanner() {
  const { t } = useI18n();
  const { decided, preferencesOpen, acceptAll, rejectNonEssential, openPreferences, closePreferences } = useConsent();

  return (
    <>
      {!decided && (
        <div
          role="region"
          aria-label={t('cookie.preferencesTitle')}
          className="fixed bottom-0 inset-x-0 z-[1150] border-t border-theme-border bg-theme-bg-surface px-6 py-5"
        >
          <div className="mx-auto max-w-4xl flex flex-col sm:flex-row items-center gap-4">
            <p className="text-xs text-theme-text-muted leading-relaxed flex-1">{t('cookie.message')}</p>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={openPreferences} className="text-xs font-medium text-theme-text-muted hover:text-theme-text underline transition-colors">
                {t('cookie.customize')}
              </button>
              <button
                onClick={rejectNonEssential}
                className="inline-flex items-center px-4 py-2 rounded-lg border border-theme-border text-xs font-medium text-theme-text"
              >
                {t('cookie.rejectNonEssential')}
              </button>
              <button
                onClick={acceptAll}
                className="inline-flex items-center px-4 py-2 rounded-lg bg-theme-accent text-theme-accent-text text-xs font-medium tracking-wide"
              >
                {t('cookie.acceptAll')}
              </button>
            </div>
          </div>
        </div>
      )}
      {preferencesOpen && <PreferencesModal onClose={closePreferences} />}
    </>
  );
}
